import { generatePartnerOtl } from '@/api/toggleExperience';
import ErrorModel from '@/api/models/ErrorModel';

/**
 * Host of the new Partners Dashboard for non-production environments
 * (local / testing / preview / preprod). The new PD's OTL consumer strips
 * `otl` and `from_old_md` from the URL after handshake, so the query
 * string doesn't linger. Overridable via `VUE_APP_NEW_PD_TEST_URL` for
 * per-env deployments (staging, preprod, ...). Default points at the
 * new PD's Cloudflare Pages preview so this button works out of the box
 * during end-to-end testing without needing to run the new PD locally.
 */
const NEW_PD_TEST_URL = process.env.VUE_APP_NEW_PD_TEST_URL ?? 'https://develop.partners-dashboard-1eo.pages.dev/';

/**
 * Production host. Overridable via `VUE_APP_NEW_PD_PRODUCTION_URL`.
 */
const NEW_PD_PRODUCTION_URL = process.env.VUE_APP_NEW_PD_PRODUCTION_URL ?? 'https://partners.zid.sa/';

/**
 * True when the running bundle points at the real production API. Every
 * other env (local, sandbox, testing, preprod, ...) resolves to the
 * dev / preview PD so previews stay isolated from the production data plane.
 */
const isProduction = (): boolean => {
  const apiBase = process.env.VUE_APP_PARTNER_DASHBOARD_ENVIRONMENT_URL ?? '';
  return apiBase.includes('api.zid.sa') && !apiBase.includes('testing-api.zid.sa');
};

/**
 * Mint an OTL, then send the browser to the new Partners Dashboard so it
 * can exchange the token for its own session.
 *
 * `from_old_md=true` triggers the new PD's one-time "welcome" modal so the
 * partner sees confirmation that the switch worked.
 *
 * Returns `false` if the OTL request failed so the caller can decide how
 * to surface the error (button re-enable, toast, ...). On success we're
 * already navigating away, so callers should leave the UI in the "loading"
 * state to prevent a fast double-click from firing a second OTL.
 */
export const switchToNewDashboard = async (): Promise<boolean> => {
  const response = await generatePartnerOtl();
  if (response instanceof ErrorModel || !response?.token) return false;

  const baseUrl = isProduction() ? NEW_PD_PRODUCTION_URL : NEW_PD_TEST_URL;
  window.location.href = `${baseUrl}?otl=${encodeURIComponent(response.token)}&from_old_md=true`;
  return true;
};
